import { useMemo, useState, type ReactNode } from "react";
import type { EngineId, Layer } from "@geosoft/viewer-sdk";
import { Button, Chip, Icon, IconButton, Stack, Text, tokens } from "@/design";
import { useViewerStore } from "@/state/useViewerStore";
import type { UnifiedSceneTreeNode } from "./scenePackage";
import {
  buildLocalSearchResults,
  firstLayerForSceneNode,
  summarizeSceneNode,
  type SceneSearchResult,
} from "./sceneExplorer";

/**
 * WP-10 — the `SimulationReadinessPanel` was extracted into
 * `frontend/src/components/readiness/SimulationReadinessPanel.tsx` so
 * the same component powers both the left-rail Twin product UX panel
 * (this re-export) and the right-rail `ReadinessToolCard`. The public
 * surface and prop shape are unchanged; existing tests under
 * `frontend/src/app/__tests__/TwinProductUx.test.tsx` keep importing
 * the symbol from this module without modification.
 */
export { SimulationReadinessPanel } from "@/components/readiness/SimulationReadinessPanel";
export type { SimulationReadinessPanelProps } from "@/components/readiness/SimulationReadinessPanel";

interface TwinGlobalSearchProps {
  readonly sceneTree?: UnifiedSceneTreeNode | null;
  readonly layers: readonly Layer[];
  readonly slotMap: Readonly<Record<string, string>>;
  readonly onSelectNode: (node: UnifiedSceneTreeNode) => void;
  readonly onFlyToLayer: (layer: Layer) => void;
}

export function TwinGlobalSearch({
  sceneTree,
  layers,
  slotMap,
  onSelectNode,
  onFlyToLayer,
}: TwinGlobalSearchProps) {
  const [query, setQuery] = useState("");
  const results = useMemo(
    () =>
      buildLocalSearchResults({
        tree: sceneTree,
        layers,
        slotMap,
        query,
        limit: 10,
      }),
    [layers, query, sceneTree, slotMap],
  );

  const applyResult = (result: SceneSearchResult) => {
    if (result.node) {
      onSelectNode(result.node);
      const layer = firstLayerForSceneNode(result.node, layers);
      if (layer?.bbox) onFlyToLayer(layer);
    }
    if (result.layer) onFlyToLayer(result.layer);
    setQuery("");
  };

  return (
    <div style={{ position: "relative", width: tokens.layout.popoverLg }}>
      <label style={{ display: "grid", gap: tokens.space[1] }}>
        <span
          style={{
            fontSize: tokens.font.size.xs,
            color: tokens.color.ink.secondary,
          }}
        >
          Search scene, objects, layers
        </span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.space[2],
            minHeight: tokens.density.touchMin,
            paddingInline: tokens.space[2],
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            background: tokens.color.surface[0],
            color: tokens.color.ink.primary,
          }}
        >
          <Icon name="search" size={20} decorative />
          <input
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder="Pump, point cloud, COG, feature id"
            style={{
              flex: 1,
              minWidth: 0,
              border: 0,
              outline: 0,
              background: "transparent",
              color: tokens.color.ink.primary,
              fontSize: tokens.font.size.sm,
            }}
          />
        </span>
      </label>

      {query.trim() ? (
        <div
          role="listbox"
          aria-label="Local scene search results"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: tokens.space[1],
            zIndex: tokens.zIndex.tooltip,
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            background: tokens.color.surface[1],
            boxShadow: tokens.elevation[2],
            overflow: "hidden",
          }}
        >
          {results.length > 0 ? (
            results.map((result) => (
              <button
                key={result.id}
                type="button"
                role="option"
                onClick={() => applyResult(result)}
                style={{
                  width: "100%",
                  minHeight: tokens.density.rowComfy,
                  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                  border: 0,
                  borderBottom: tokens.border.thin,
                  background: tokens.color.surface[1],
                  color: tokens.color.ink.primary,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <Stack direction="row" align="center" gap={2}>
                  <Icon
                    name={result.kind === "node" ? "account_tree" : "layers"}
                    size={20}
                    decorative
                  />
                  <span style={{ minWidth: 0 }}>
                    <strong
                      style={{
                        display: "block",
                        fontSize: tokens.font.size.sm,
                      }}
                    >
                      {result.label}
                    </strong>
                    <span
                      style={{
                        color: tokens.color.ink.secondary,
                        fontSize: tokens.font.size.xs,
                      }}
                    >
                      {result.detail}
                    </span>
                  </span>
                </Stack>
              </button>
            ))
          ) : (
            <div
              role="status"
              style={{
                padding: tokens.space[3],
                color: tokens.color.ink.secondary,
                fontSize: tokens.font.size.sm,
              }}
            >
              No local scene-index or layer results. Address and POI providers
              are reserved for a later phase.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

interface SemanticPropertyCardProps {
  readonly node: UnifiedSceneTreeNode | null;
  readonly layers: readonly Layer[];
  readonly onFlyToLayer: (layer: Layer) => void;
  readonly onIsolateLayer: (layer: Layer) => void;
  readonly onClose: () => void;
}

export function SemanticPropertyCard({
  node,
  layers,
  onFlyToLayer,
  onIsolateLayer,
  onClose,
}: SemanticPropertyCardProps) {
  const [reviewOverrides, setReviewOverrides] = useState<
    Record<string, "approved" | "rejected">
  >({});
  if (!node) return null;
  const layer = firstLayerForSceneNode(node, layers);
  const summary = summarizeSceneNode(node, layer);
  const updateReview = (factId: string, status: "approved" | "rejected") => {
    setReviewOverrides((current) => ({ ...current, [factId]: status }));
  };

  return (
    <aside
      aria-label="Semantic property card"
      style={{
        position: "absolute",
        right: tokens.space[3],
        top: tokens.space[7],
        zIndex: tokens.zIndex.panel,
        width: tokens.layout.popoverLg,
        maxHeight: `calc(100vh - ${tokens.space[8]}px)`,
        overflow: "auto",
        border: tokens.border.thin,
        borderRadius: tokens.radius.lg,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        boxShadow: tokens.elevation[2],
        padding: tokens.space[3],
      }}
    >
      <Stack direction="row" align="center" justify="space-between" gap={2}>
        <Stack direction="row" align="center" gap={2}>
          <Icon name="account_tree" size={24} decorative />
          <div>
            <Text variant="subtitle" as="h2" style={{ margin: 0 }}>
              {node.label}
            </Text>
            <Text variant="caption" as="p" style={{ margin: 0 }}>
              {node.kind}
            </Text>
          </div>
        </Stack>
        <IconButton aria-label="Close semantic property card" onClick={onClose}>
          <Icon name="close" size={20} decorative />
        </IconButton>
      </Stack>

      <Stack
        direction="row"
        gap={1}
        style={{ flexWrap: "wrap", marginTop: tokens.space[2] }}
      >
        {node.readiness.length > 0 ? (
          node.readiness.map((state) => (
            <Chip
              key={state}
              icon="check_circle"
              label={state}
              variant="success"
            />
          ))
        ) : (
          <Chip icon="info" label="No readiness state" variant="neutral" />
        )}
        {summary.integrationStates.map((state) => (
          <Chip
            key={`${state.state}:${state.providerType ?? ""}:${state.status ?? ""}`}
            icon={
              state.state === "permission-denied"
                ? "lock"
                : state.state === "offline"
                  ? "cloud_off"
                  : "sync_problem"
            }
            label={state.label}
            variant={
              state.state === "permission-denied" || state.state === "conflict"
                ? "error"
                : "warning"
            }
          />
        ))}
      </Stack>

      <div
        style={{
          marginTop: tokens.space[3],
          display: "grid",
          gap: tokens.space[2],
        }}
      >
        {summary.facts.map((fact) => (
          <PropertyRow
            key={`${fact.label}:${fact.value}`}
            label={fact.label}
            value={fact.value}
          />
        ))}
      </div>

      <SectionTitle title="AI candidate facts" />
      {summary.candidateFacts.length > 0 ? (
        <Stack direction="column" gap={1}>
          {summary.candidateFacts.slice(0, 8).map((fact) => {
            const reviewStatus = reviewOverrides[fact.id] ?? fact.reviewStatus;
            const isApproved = reviewStatus === "approved";
            const isRejected = reviewStatus === "rejected";
            return (
              <div key={fact.id} style={miniCardStyle}>
                <Stack direction="row" align="center" justify="space-between" gap={2}>
                  <div style={{ minWidth: 0 }}>
                    <strong
                      style={{
                        display: "block",
                        color: tokens.color.ink.primary,
                        fontSize: tokens.font.size.sm,
                      }}
                    >
                      {fact.predicate}: {fact.literalValue ?? fact.objectNodeId ?? "Candidate"}
                    </strong>
                    <span
                      style={{
                        color: tokens.color.ink.secondary,
                        fontSize: tokens.font.size.xs,
                      }}
                    >
                      {fact.factType} / {fact.source} / {Math.round(fact.confidence * 100)}%
                    </span>
                  </div>
                  <Chip
                    icon={isApproved ? "task_alt" : isRejected ? "block" : "pending"}
                    label={reviewStatus}
                    variant={isApproved ? "success" : isRejected ? "error" : "warning"}
                  />
                </Stack>
                <Stack
                  direction="row"
                  gap={1}
                  style={{ marginTop: tokens.space[2], flexWrap: "wrap" }}
                >
                  <Button
                    variant="secondary"
                    disabled={isApproved}
                    onClick={() => updateReview(fact.id, "approved")}
                  >
                    Approve locally
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={isRejected}
                    onClick={() => updateReview(fact.id, "rejected")}
                  >
                    Reject locally
                  </Button>
                </Stack>
              </div>
            );
          })}
        </Stack>
      ) : (
        <EmptyText>
          No AI or rule-inferred candidate facts are attached to this node.
        </EmptyText>
      )}

      <SectionTitle title="Geometry refs" />
      {summary.geometryRefs.length > 0 ? (
        <Stack direction="column" gap={1}>
          {summary.geometryRefs.slice(0, 6).map((ref, index) => (
            <div
              key={`${ref.sceneItemId ?? "item"}:${ref.featureId ?? index}`}
              style={miniCardStyle}
            >
              <PropertyRow
                label="Scene item"
                value={ref.sceneItemId ?? "Not specified"}
              />
              {ref.featureId ? (
                <PropertyRow label="Feature ID" value={ref.featureId} />
              ) : null}
              {ref.contentUri ? (
                <PropertyRow label="Content URI" value={ref.contentUri} />
              ) : null}
            </div>
          ))}
        </Stack>
      ) : (
        <EmptyText>
          No geometry or feature-id references are attached to this node.
        </EmptyText>
      )}

      <SectionTitle title="Linked context" />
      {summary.links.length > 0 ? (
        <Stack direction="column" gap={1}>
          {summary.links.slice(0, 6).map((link) => (
            <div key={`${link.label}:${link.detail}`} style={miniCardStyle}>
              <strong
                style={{ display: "block", fontSize: tokens.font.size.sm }}
              >
                {link.label}
              </strong>
              <span
                style={{
                  color: tokens.color.ink.secondary,
                  fontSize: tokens.font.size.xs,
                }}
              >
                {link.detail}
              </span>
            </div>
          ))}
        </Stack>
      ) : (
        <EmptyText>
          No documents, sensors, artifacts, or integration links are attached
          yet.
        </EmptyText>
      )}

      <Stack
        direction="row"
        gap={2}
        style={{ marginTop: tokens.space[3], flexWrap: "wrap" }}
      >
        <Button
          variant="primary"
          disabled={!layer?.bbox}
          onClick={() => layer && onFlyToLayer(layer)}
        >
          Fly to
        </Button>
        <Button
          variant="secondary"
          disabled={!layer}
          onClick={() => layer && onIsolateLayer(layer)}
        >
          Isolate
        </Button>
        <Button variant="ghost" onClick={() => copyText(node.id)}>
          Copy id
        </Button>
        <Button
          variant="ghost"
          disabled={!layer}
          onClick={() => layer && onFlyToLayer(layer)}
        >
          Open related layer
        </Button>
      </Stack>
    </aside>
  );
}

export function TwinSavedViewsPanel({ gtwinId }: { readonly gtwinId: string }) {
  const savedViewsFromHook = useViewerStore((state) => state.savedViews);
  const savedViews =
    savedViewsFromHook.length > 0
      ? savedViewsFromHook
      : useViewerStore.getState().savedViews;
  const saveCurrentView = useViewerStore.getState().saveCurrentView;
  const restoreSavedView = useViewerStore.getState().restoreSavedView;
  const removeSavedView = useViewerStore.getState().removeSavedView;
  const [name, setName] = useState("");
  const [notice, setNotice] = useState("");

  const save = () => {
    const viewName = name.trim() || `Viewer view ${savedViews.length + 1}`;
    saveCurrentView(viewName);
    setName("");
    setNotice(`Saved ${viewName}.`);
  };

  return (
    <section
      aria-label="Saved views and share state"
      style={{ padding: tokens.space[3], borderTop: tokens.border.thin }}
    >
      <Stack direction="row" align="center" justify="space-between" gap={2}>
        <Text variant="subtitle" as="h2" style={{ margin: 0 }}>
          Views and share
        </Text>
        <Chip
          icon="bookmark"
          label={`${savedViews.length} saved`}
          variant="neutral"
        />
      </Stack>
      <Text
        variant="caption"
        as="p"
        style={{ margin: `${tokens.space[1]}px 0 ${tokens.space[2]}px` }}
      >
        Local camera, visibility, and time state for this gTwin route. Share
        keeps the current route contract.
      </Text>
      <Stack direction="row" gap={2}>
        <input
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          placeholder="View name"
          aria-label="Saved view name"
          style={inputStyle}
        />
        <Button variant="primary" onClick={save}>
          Save
        </Button>
      </Stack>
      <Stack direction="row" gap={2} style={{ marginTop: tokens.space[2] }}>
        <Button
          variant="secondary"
          onClick={() =>
            copyText(
              typeof window === "undefined" ? gtwinId : window.location.href,
            )
          }
        >
          Copy share link
        </Button>
      </Stack>
      {savedViews.length > 0 ? (
        <Stack
          direction="column"
          gap={1}
          style={{ marginTop: tokens.space[2] }}
        >
          {savedViews.slice(0, 4).map((view) => (
            <div key={view.id} style={miniCardStyle}>
              <Stack
                direction="row"
                align="center"
                justify="space-between"
                gap={2}
              >
                <button
                  type="button"
                  onClick={() => restoreSavedView(view.id)}
                  style={plainButtonStyle}
                >
                  <strong
                    style={{
                      display: "block",
                      color: tokens.color.ink.primary,
                    }}
                  >
                    {view.name}
                  </strong>
                  <span
                    style={{
                      color: tokens.color.ink.secondary,
                      fontSize: tokens.font.size.xs,
                    }}
                  >
                    {view.visibleLayerIds.length} layers /{" "}
                    {new Date(view.createdAt).toLocaleDateString()}
                  </span>
                </button>
                <IconButton
                  aria-label={`Delete saved view ${view.name}`}
                  dense
                  onClick={() => removeSavedView(view.id)}
                >
                  <Icon name="delete" size={20} decorative />
                </IconButton>
              </Stack>
            </div>
          ))}
        </Stack>
      ) : (
        <EmptyText>No saved views yet.</EmptyText>
      )}
      {notice ? (
        <p
          role="status"
          style={{
            margin: `${tokens.space[2]}px 0 0`,
            color: tokens.color.semantic.success,
          }}
        >
          {notice}
        </p>
      ) : null}
    </section>
  );
}

interface AnalysisToolbarProps {
  readonly engine: EngineId;
  readonly onOpenSettings: () => void;
}

export function AnalysisToolbar({
  engine,
  onOpenSettings,
}: AnalysisToolbarProps) {
  const mapTool = useViewerStore((state) => state.mapTool);
  const setMapTool = useViewerStore((state) => state.setMapTool);
  const hasNative3DTools = engine === "giro3d";
  return (
    <nav
      aria-label="Product analysis tools"
      style={{
        position: "absolute",
        left: tokens.space[3],
        top: tokens.space[3],
        zIndex: tokens.zIndex.panel,
        display: "flex",
        gap: tokens.space[2],
        flexWrap: "wrap",
        maxWidth: `calc(100% - ${tokens.layout.popoverLg}px)`,
      }}
    >
      <ToolGroup label="Inspect">
        <ToolButton
          icon="info"
          label="Pick/info"
          active={mapTool === "select"}
          onClick={() => setMapTool("select")}
        />
      </ToolGroup>
      <ToolGroup label="Measure">
        <ToolButton
          icon="straighten"
          label="Measure"
          active={mapTool === "measure"}
          onClick={() => setMapTool("measure")}
        />
      </ToolGroup>
      <ToolGroup label="3D">
        <ToolButton
          icon="content_cut"
          label={hasNative3DTools ? "Clip in 3D toolbar" : "Clip/slice"}
          disabled={!hasNative3DTools}
          disabledReason={
            hasNative3DTools ? undefined : "Coming soon for this engine"
          }
          onClick={() => undefined}
        />
      </ToolGroup>
      <ToolGroup label="Future analysis">
        <ToolButton
          icon="compare_arrows"
          label="Compare/swipe"
          disabled
          disabledReason="Coming soon"
          onClick={() => undefined}
        />
        <ToolButton
          icon="wb_sunny"
          label="Sun/shadow"
          disabled
          disabledReason="Coming soon"
          onClick={() => undefined}
        />
        <ToolButton
          icon="visibility"
          label="Viewshed"
          disabled
          disabledReason="Coming soon"
          onClick={() => undefined}
        />
        <ToolButton
          icon="landscape"
          label="Elevation profile"
          disabled
          disabledReason="Coming soon"
          onClick={() => undefined}
        />
      </ToolGroup>
      <ToolGroup label="Viewer">
        <ToolButton icon="settings" label="Settings" onClick={onOpenSettings} />
      </ToolGroup>
    </nav>
  );
}

export function ViewerSettingsPanel({
  engine,
  onClose,
}: {
  readonly engine: EngineId;
  readonly onClose: () => void;
}) {
  const density = useViewerStore((state) => state.pointCloudDensityTarget);
  const setDensity = useViewerStore(
    (state) => state.setPointCloudDensityTarget,
  );
  const canvasTone = useViewerStore((state) => state.threeDCanvasTone);
  return (
    <aside
      aria-label="Viewer settings"
      style={{
        position: "absolute",
        right: tokens.space[3],
        top: tokens.space[3],
        zIndex: tokens.zIndex.dock,
        width: tokens.layout.popoverMd,
        border: tokens.border.thin,
        borderRadius: tokens.radius.lg,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        boxShadow: tokens.elevation[2],
        padding: tokens.space[3],
      }}
    >
      <Stack direction="row" align="center" justify="space-between" gap={2}>
        <Text variant="subtitle" as="h2" style={{ margin: 0 }}>
          Viewer settings
        </Text>
        <IconButton aria-label="Close viewer settings" onClick={onClose}>
          <Icon name="close" size={20} decorative />
        </IconButton>
      </Stack>

      <SectionTitle title="Quality" />
      <label style={{ display: "grid", gap: tokens.space[1] }}>
        <span
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
          }}
        >
          Point budget target: {Math.round(density * 100)}%
        </span>
        <input
          type="range"
          min={0.1}
          max={1}
          step={0.05}
          value={density}
          onChange={(event) => setDensity(Number(event.currentTarget.value))}
          aria-label="Point cloud density target"
          style={{ accentColor: tokens.color.accent.primary }}
        />
      </label>

      <SectionTitle title="Navigation" />
      <div style={miniCardStyle}>
        <PropertyRow label="Engine" value={engine} />
        <PropertyRow
          label="Navigation"
          value={
            engine === "giro3d"
              ? "Orbit, pick, measure, section controls"
              : "Pan, zoom, inspect"
          }
        />
      </div>

      <SectionTitle title="Display" />
      <div style={miniCardStyle}>
        <PropertyRow label="Canvas tone" value={canvasTone} />
        <PropertyRow label="Theme" value="Platform theme placeholder" />
        <PropertyRow label="Language" value="Locale selector placeholder" />
        <PropertyRow
          label="Layer display"
          value="Opacity and visibility are controlled in the catalog"
        />
      </div>
    </aside>
  );
}

function ToolGroup({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: tokens.space[1],
        padding: tokens.space[1],
        border: tokens.border.thin,
        borderRadius: tokens.radius.lg,
        background: tokens.color.surface[1],
        boxShadow: tokens.elevation[1],
      }}
    >
      <span
        style={{
          color: tokens.color.ink.secondary,
          fontSize: tokens.font.size.xs,
          paddingInline: tokens.space[1],
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function ToolButton({
  icon,
  label,
  active,
  disabled,
  disabledReason,
  onClick,
}: {
  readonly icon: Parameters<typeof Icon>[0]["name"];
  readonly label: string;
  readonly active?: boolean;
  readonly disabled?: boolean;
  readonly disabledReason?: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={disabledReason ?? label}
      aria-label={disabledReason ? `${label}: ${disabledReason}` : label}
      onClick={onClick}
      style={{
        minHeight: tokens.density.touchMin,
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        border: tokens.border.thin,
        borderRadius: tokens.radius.input,
        background: active
          ? tokens.color.accent.primary
          : tokens.color.surface[0],
        color: disabled
          ? tokens.color.ink.muted
          : active
            ? tokens.color.accent.on
            : tokens.color.ink.primary,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: tokens.space[1],
        fontSize: tokens.font.size.xs,
      }}
    >
      <Icon name={icon} size={20} decorative />
      <span>{label}</span>
      {disabled ? (
        <span style={{ color: tokens.color.ink.muted }}>(soon)</span>
      ) : null}
    </button>
  );
}

function PropertyRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 0.42fr) minmax(0, 0.58fr)",
        gap: tokens.space[2],
      }}
    >
      <span
        style={{
          color: tokens.color.ink.secondary,
          fontSize: tokens.font.size.xs,
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: tokens.color.ink.primary,
          fontSize: tokens.font.size.xs,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SectionTitle({ title }: { readonly title: string }) {
  return (
    <h3
      style={{
        margin: `${tokens.space[3]}px 0 ${tokens.space[2]}px`,
        fontSize: tokens.font.size.sm,
      }}
    >
      {title}
    </h3>
  );
}

function EmptyText({ children }: { readonly children: ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        color: tokens.color.ink.secondary,
        fontSize: tokens.font.size.sm,
      }}
    >
      {children}
    </p>
  );
}

function copyText(value: string): void {
  if (typeof navigator === "undefined") return;
  void navigator.clipboard?.writeText(value);
}

const inputStyle = {
  minHeight: tokens.density.touchMin,
  minWidth: 0,
  flex: 1,
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
  background: tokens.color.surface[0],
  color: tokens.color.ink.primary,
  paddingInline: tokens.space[2],
};

const miniCardStyle = {
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  background: tokens.color.surface[0],
  padding: tokens.space[2],
};

const plainButtonStyle = {
  border: 0,
  background: "transparent",
  color: tokens.color.ink.primary,
  textAlign: "left" as const,
  padding: 0,
  cursor: "pointer",
  flex: 1,
};
